import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminChangePassword() {
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (passwords.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("adminToken");

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/change-password`,
        {
          old_password: passwords.oldPassword,
          new_password: passwords.newPassword
        },
        { params: { token } }
      );
      
      toast.success("Password changed successfully!");
      setPasswords({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => navigate("/admin"), 2000);
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center">
          <Link to="/admin">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="glass-effect rounded-2xl p-8 md:p-12">
          <div className="flex items-center mb-8">
            <Lock className="h-8 w-8 text-primary mr-3" />
            <h1 className="font-heading text-3xl font-bold text-text-primary">
              Change Password
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-text-primary mb-2 font-semibold">
                Current Password
              </label>
              <Input
                type="password"
                value={passwords.oldPassword}
                onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                required
                className="w-full"
                placeholder="Enter current password"
                data-testid="old-password-input"
              />
            </div>

            <div>
              <label className="block text-text-primary mb-2 font-semibold">
                New Password
              </label>
              <Input
                type="password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                required
                minLength={6}
                className="w-full"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-text-primary mb-2 font-semibold">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                required
                className="w-full"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full text-lg py-6"
                data-testid="change-password-submit"
              >
                {loading ? "Changing Password..." : "Change Password"}
              </Button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-background-dark rounded-lg">
            <h3 className="text-sm font-semibold text-text-primary mb-2">Password Requirements:</h3>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>• Minimum 6 characters</li>
              <li>• Should be different from current password</li>
              <li>• Use a strong, unique password</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
